export const UNDO_REDO_DEPTH = 100;

function deepClone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

export function captureHistorySnapshot(state) {
  return {
    clips: deepClone(state.clips || []),
    tracks: deepClone(state.tracks || []),
    trackVolumes: deepClone(state.trackVolumes || {}),
    mutedTracks: deepClone(state.mutedTracks || {}),
    assets: deepClone(state.assets || {}),
  };
}

export function applyHistorySnapshot(state, snapshot) {
  return {
    ...state,
    clips: snapshot.clips,
    tracks: snapshot.tracks,
    trackVolumes: snapshot.trackVolumes,
    mutedTracks: snapshot.mutedTracks,
    assets: snapshot.assets,
  };
}

export function areSnapshotsEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function createHistoryState() {
  return {
    undoStack: [],
    redoStack: [],
    historyTransaction: null,
  };
}

export function pushUndoSnapshot(state, snapshot, depth = UNDO_REDO_DEPTH) {
  const nextUndo = [...state.undoStack, snapshot];
  const boundedUndo = nextUndo.length > depth ? nextUndo.slice(nextUndo.length - depth) : nextUndo;
  return {
    ...state,
    undoStack: boundedUndo,
    redoStack: [],
  };
}

export function beginHistoryTransaction(state, label, snapshot) {
  if (state.historyTransaction) return state;
  return {
    ...state,
    historyTransaction: {
      label,
      before: snapshot,
    },
  };
}

export function commitHistoryTransaction(state, afterSnapshot, depth = UNDO_REDO_DEPTH) {
  const tx = state.historyTransaction;
  if (!tx) return state;

  if (areSnapshotsEqual(tx.before, afterSnapshot)) {
    return {
      ...state,
      historyTransaction: null,
    };
  }

  const next = pushUndoSnapshot(state, tx.before, depth);
  return {
    ...next,
    historyTransaction: null,
  };
}

export function cancelHistoryTransaction(state) {
  if (!state.historyTransaction) return state;
  return {
    ...state,
    historyTransaction: null,
  };
}

export function getUndoResult(state, currentSnapshot, depth = UNDO_REDO_DEPTH) {
  if (!state.undoStack.length) return null;

  const previous = state.undoStack[state.undoStack.length - 1];
  const undoStack = state.undoStack.slice(0, -1);
  const nextRedo = [...state.redoStack, currentSnapshot];
  const redoStack = nextRedo.length > depth ? nextRedo.slice(nextRedo.length - depth) : nextRedo;

  return {
    snapshot: previous,
    undoStack,
    redoStack,
  };
}

export function getRedoResult(state, currentSnapshot, depth = UNDO_REDO_DEPTH) {
  if (!state.redoStack.length) return null;

  const nextSnapshot = state.redoStack[state.redoStack.length - 1];
  const redoStack = state.redoStack.slice(0, -1);
  const nextUndo = [...state.undoStack, currentSnapshot];
  const undoStack = nextUndo.length > depth ? nextUndo.slice(nextUndo.length - depth) : nextUndo;

  return {
    snapshot: nextSnapshot,
    undoStack,
    redoStack,
  };
}
